import React from "react";
import { Box } from "@mui/material";

interface LargePDFViewerProps {
  /**
   * URL to the PDF file
   */
  pdfUrl: string;

  /**
   * Optional: Custom title for the viewer
   */
  title?: string;

  /**
   * Optional: Height of the container (default: 100vh)
   */
  height?: string | number;

  /**
   * Optional: Width of the container (default: 100%)
   */
  width?: string | number;

  /**
   * Optional: Additional CSS classes to apply
   */
  className?: string;

  /**
   * Optional: Callback when PDF fails to load
   */
  onError?: (error: Error) => void;
}

/**
 * LargePDFViewer Component
 *
 * A simple PDF viewer component that uses native browser PDF rendering.
 * This approach delegates PDF rendering to the browser's native capabilities,
 * which are optimized for handling large files efficiently.
 *
 * Features:
 * - Native browser PDF viewer (Chrome, Firefox, Safari all have built-in support)
 * - Automatic zoom, search, and print functionality from the browser
 * - Minimal memory footprint
 * - Fast loading
 * - Responsive design
 *
 * @example
 * ```tsx
 * <LargePDFViewer
 *   pdfUrl="/path/to/large-file.pdf"
 *   title="My Large PDF"
 *   height="100vh"
 * />
 * ```
 */
const LargePDFViewer: React.FC<LargePDFViewerProps> = ({
  pdfUrl,
  title,
  height = "100vh",
  width = "100%",
  className,
  onError,
}) => {
  const handleIframeError = () => {
    const errorMessage =
      "Failed to load PDF. Please check the file URL and try again.";
    onError?.(new Error(errorMessage));
  };

  // Use the PDF URL directly (relative or absolute path)
  const pdfSrc = pdfUrl.startsWith("http") ? pdfUrl : pdfUrl;

  return (
    <Box
      className={className}
      sx={{
        width,
        height,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        backgroundColor: "#f5f5f5",
      }}
    >
      {/* Title (optional) */}
      {title && (
        <Box
          sx={{
            padding: "12px 16px",
            backgroundColor: "#fff",
            borderBottom: "1px solid #ddd",
            fontSize: "14px",
            fontWeight: 600,
            color: "#333",
          }}
        >
          {title}
        </Box>
      )}

      {/* Native PDF Viewer */}
      <iframe
        src={pdfSrc}
        width="100%"
        height="100%"
        style={{ border: "none" }}
        title={title || "PDF Viewer"}
        onError={handleIframeError}
        allowFullScreen
      />
    </Box>
  );
};

export default LargePDFViewer;
